import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../hooks/useFetch';

/**
 * Apple Sign-In redirect-mode landing page.
 *
 * When the iOS WebView (or any browser using non-popup mode) finishes
 * the Sign in with Apple flow, Apple POSTs back to the backend
 * `/api/auth/apple/callback`, which 302-redirects here with the
 * `id_token`, `code`, and optional first-sign-in `user` JSON in the
 * query string. We complete the JWT exchange the same way the popup
 * mode does and route the user to /library (or /account?welcome=1).
 *
 * This route is critical for Apple App Store reviewers running on iPad:
 * popup-based SIWA silently fails inside WKWebView. The redirect path
 * is the only flow Apple guarantees will work on every iOS device.
 */
export default function AppleCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const idToken = params.get('id_token');
    const userJson = params.get('user');

    if (!idToken) {
      setError(
        'Apple did not return an identity token. Please try signing in again or use another option.'
      );
      return;
    }

    let userPayload = null;
    if (userJson) {
      try {
        userPayload = JSON.parse(userJson);
      } catch (_) {
        userPayload = null;
      }
    }

    (async () => {
      try {
        const { data, error: apiError, status } = await apiFetch(
          '/api/auth/apple/exchange',
          {
            method: 'POST',
            body: { id_token: idToken, user: userPayload },
          }
        );

        if (apiError || !data?.token) {
          // eslint-disable-next-line no-console
          console.error('[AppleCallback] exchange failed', { status, apiError });
          setError(
            apiError ||
              `Apple sign-in could not be completed (HTTP ${status}). Please try again.`
          );
          // Strip the (now-spent) id_token from the URL so a back-nav
          // doesn't replay the same bad token.
          window.history.replaceState({}, '', '/auth/apple/callback');
          return;
        }

        localStorage.setItem('auth_token', data.token);
        toast.success('Signed in with Apple');
        // Clean the URL so the id_token is not persisted in history
        window.history.replaceState({}, '', '/auth/apple/callback');
        navigate(data.is_new_user ? '/account?welcome=1' : '/library', {
          replace: true,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[AppleCallback] exception', e);
        setError(
          e?.message ||
            'Something went wrong completing Apple sign-in. Please try again.'
        );
      }
    })();
  }, [navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background p-6"
      data-testid="apple-callback-page"
    >
      <div className="max-w-sm w-full text-center space-y-4">
        {error ? (
          <>
            <AlertTriangle
              className="h-10 w-10 text-destructive mx-auto"
              aria-hidden="true"
            />
            <h1 className="text-lg font-semibold" data-testid="apple-callback-error-title">
              Sign in with Apple failed
            </h1>
            <p
              className="text-sm text-muted-foreground"
              data-testid="apple-callback-error-message"
            >
              {error}
            </p>
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="mt-2 inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90"
              data-testid="apple-callback-retry-btn"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <Loader2
              className="h-10 w-10 animate-spin text-primary mx-auto"
              aria-hidden="true"
            />
            <h1 className="text-lg font-semibold">Finishing Apple sign-in…</h1>
            <p className="text-sm text-muted-foreground">
              Just a moment — securely linking your Apple ID.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
